import { useCallback, useEffect, useRef, useState } from 'react';
import { generatePath, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AutoGrowTextarea } from '@/components/ui/autoGrowTextarea';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { useQueryClient } from '@tanstack/react-query';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { PageContainer } from '@/components/layout/PageContainer';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { FilePreviewModal } from '@/components/common/FilePreviewModal';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import { SearchableSelect } from '@/components/ui/searchableSelect';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import { usePatientQuery } from '@/hooks/usePatientsQueries';
import { useBodyAreasQuery, useTreatmentsQuery } from '@/hooks/useTreatmentLibraryQueries';
import {
  useAppointmentSessionNoteQuery,
  usePatientSessionNotesQuery,
  useSessionNoteQuery,
} from '@/hooks/useSessionNoteQueries';
import {
  useCreateSessionNoteMutation,
  useRemoveSessionNotePhotoMutation,
  useUpdateSessionNoteMutation,
  useUploadSessionNotePhotoMutation,
} from '@/hooks/useSessionNoteMutations';
import { fetchLastDeviceSettings } from '@/api/sessionNotes/sessionNotesApi';
import type {
  SessionNote,
  SessionNotePhoto,
  SessionNoteWritePayload,
} from '@/api/sessionNotes/sessionNotesApi.types';
import type { Treatment } from '@/api/treatmentLibrary/treatmentLibraryApi.types';
import { buildLastDeviceSettingsQueryKey } from '@/constants/queryKeys';
import type { DeviceBrief } from '@/api/treatmentLibrary/treatmentLibraryApi.types';
import { toEditableDeviceSettingValue } from '@/utils/durationFormatting';
import { MEDIA_IMAGE_CONTENT_TYPES, buildAcceptAttribute } from '@/constants/media';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { useAssistantSessionNoteForm } from '@/components/assistant/sessionNote/useAssistantSessionNoteForm';
import type { SessionNoteDraft } from '@/api/assistantVoice/assistantVoiceApi.types';
import { SummaryCard } from './SummaryCard';
import { applySessionNoteDraft } from './applySessionNoteDraft';
import {
  createEmptyDeviceSettingSets,
  flattenDeviceSettingSets,
  groupDeviceSettingsIntoSets,
} from './deviceSettingSets';
import { TreatmentRow } from './TreatmentRow';
import type { EditorTreatment } from './types';

function localizedName(nameEn: string, namePl: string): string {
  if (i18next.language === 'pl' && namePl.length > 0) {
    return namePl;
  }
  return nameEn;
}

const PAIN_SCORES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

let localTreatmentKeyCounter = 0;
function nextTreatmentKey(): string {
  localTreatmentKeyCounter = localTreatmentKeyCounter + 1;
  return 'treatment-' + localTreatmentKeyCounter;
}

export function SessionNoteEditor() {
  const { t } = useTranslation(['patients', 'consultations', 'common']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { patientId, noteId } = useParams<{ patientId: string; noteId: string }>();
  const [searchParams] = useSearchParams();
  const resolvedPatientId = patientId === undefined ? '' : patientId;
  const routeNoteId = noteId === undefined ? 'new' : noteId;
  const isNew = routeNoteId === 'new';
  const appointmentId = searchParams.get('appointmentId');

  const patientQuery = usePatientQuery(resolvedPatientId);
  const treatmentsQuery = useTreatmentsQuery('', null, false, true);
  const bodyAreasQuery = useBodyAreasQuery(true);
  const noteQuery = useSessionNoteQuery(isNew ? '' : routeNoteId);
  const appointmentNoteQuery = useAppointmentSessionNoteQuery(
    appointmentId === null ? '' : appointmentId,
    isNew && appointmentId !== null
  );
  const patientNotesQuery = usePatientSessionNotesQuery(resolvedPatientId, 1, null);

  const createMutation = useCreateSessionNoteMutation();
  const updateMutation = useUpdateSessionNoteMutation();
  const uploadPhotoMutation = useUploadSessionNotePhotoMutation();
  const removePhotoMutation = useRemoveSessionNotePhotoMutation();
  const photoInputRef = useRef<HTMLInputElement>(null);

  const [isInitialized, setIsInitialized] = useState(false);
  const [photos, setPhotos] = useState<SessionNotePhoto[]>([]);
  const [previewedPhoto, setPreviewedPhoto] = useState<SessionNotePhoto | null>(null);
  const [resolvedNoteId, setResolvedNoteId] = useState<string | null>(null);
  const [linkedAppointmentId, setLinkedAppointmentId] = useState<string | null>(appointmentId);
  const [status, setStatus] = useState('draft');
  const [painScore, setPainScore] = useState('');
  const [assessment, setAssessment] = useState('');
  const [privateNotes, setPrivateNotes] = useState('');
  const [bodyAreaIds, setBodyAreaIds] = useState<string[]>([]);
  const [treatments, setTreatments] = useState<EditorTreatment[]>([]);
  const [pickerValue, setPickerValue] = useState('');
  const [baselineSnapshot, setBaselineSnapshot] = useState<string | null>(null);
  const [isLeaveConfirmOpen, setIsLeaveConfirmOpen] = useState(false);
  const [isSaveAsDraftConfirmOpen, setIsSaveAsDraftConfirmOpen] = useState(false);
  const [pendingSaveAction, setPendingSaveAction] = useState<'complete' | 'draft' | null>(null);

  const libraryTreatments = treatmentsQuery.data === undefined ? [] : treatmentsQuery.data;
  const bodyAreas = bodyAreasQuery.data === undefined ? [] : bodyAreasQuery.data;

  function withLocalizedTreatmentName(treatment: EditorTreatment): EditorTreatment {
    const library = libraryTreatments.find(function match(candidate) {
      return candidate.id === treatment.treatmentId;
    });
    if (library === undefined) {
      return treatment;
    }
    return { ...treatment, treatmentName: localizedName(library.nameEn, library.namePl) };
  }

  const loadFromNote = useCallback(
    function load(note: SessionNote) {
      const currentLibrary = treatmentsQuery.data === undefined ? [] : treatmentsQuery.data;
      setResolvedNoteId(note.id);
      setLinkedAppointmentId(note.appointmentId);
      setStatus(note.status);
      setPainScore(note.painScore === null ? '' : String(note.painScore));
      setAssessment(note.assessment === null ? '' : note.assessment);
      setPrivateNotes(note.privateNotes === null ? '' : note.privateNotes);
      setBodyAreaIds(note.bodyAreaIds);
      setPhotos(note.photos);
      setTreatments(
        note.treatments.map(function toEditor(serverTreatment) {
          const library = currentLibrary.find(function match(candidate) {
            return candidate.id === serverTreatment.treatmentId;
          });
          const deviceSettingSets = groupDeviceSettingsIntoSets(
            serverTreatment.deviceSettings,
            function toEditable(setting, deviceSettingFieldId) {
              return toEditableDeviceSettingValue(
                setting.value,
                setting.fieldUnit,
                findSettingFieldType(
                  library === undefined ? null : library.device,
                  deviceSettingFieldId
                )
              );
            }
          );
          return {
            key: nextTreatmentKey(),
            treatmentId: serverTreatment.treatmentId,
            treatmentName: serverTreatment.treatmentName,
            contraindication: library === undefined ? null : library.contraindication,
            device: library === undefined ? null : library.device,
            bodyAreaId: serverTreatment.bodyAreaId === null ? '' : serverTreatment.bodyAreaId,
            outcome: serverTreatment.outcome,
            response: serverTreatment.response === null ? '' : serverTreatment.response,
            deviceSettingSets,
            recalledFromLast: false,
          };
        })
      );
    },
    [treatmentsQuery.data]
  );

  useEffect(
    function initialize() {
      if (isInitialized || treatmentsQuery.data === undefined) {
        return;
      }
      if (!isNew) {
        if (noteQuery.data !== undefined) {
          loadFromNote(noteQuery.data);
          setIsInitialized(true);
        }
        return;
      }
      if (appointmentId !== null && appointmentNoteQuery.isLoading) {
        return;
      }
      if (appointmentId !== null && appointmentNoteQuery.data !== undefined) {
        loadFromNote(appointmentNoteQuery.data);
      }
      setIsInitialized(true);
    },
    [
      isInitialized,
      isNew,
      appointmentId,
      treatmentsQuery.data,
      noteQuery.data,
      appointmentNoteQuery.data,
      appointmentNoteQuery.isLoading,
      loadFromNote,
    ]
  );

  function toggleBodyArea(id: string) {
    setBodyAreaIds(function update(current) {
      if (current.includes(id)) {
        return current.filter(function keep(existing) {
          return existing !== id;
        });
      }
      return [...current, id];
    });
  }

  async function addTreatment(treatment: Treatment) {
    let deviceSettingSets = createEmptyDeviceSettingSets();
    let recalledFromLast = false;
    const device = treatment.device;
    if (device !== null) {
      const previous = await queryClient.fetchQuery({
        queryKey: buildLastDeviceSettingsQueryKey(resolvedPatientId, treatment.id),
        queryFn: function loadLast() {
          return fetchLastDeviceSettings(resolvedPatientId, treatment.id);
        },
      });
      deviceSettingSets = groupDeviceSettingsIntoSets(
        previous,
        function toEditable(setting, deviceSettingFieldId) {
          return toEditableDeviceSettingValue(
            setting.value,
            setting.fieldUnit,
            findSettingFieldType(device, deviceSettingFieldId)
          );
        }
      );
      recalledFromLast = previous.some(function hasField(setting) {
        return setting.deviceSettingFieldId !== null;
      });
    }
    setTreatments(function append(current) {
      return [
        ...current,
        {
          key: nextTreatmentKey(),
          treatmentId: treatment.id,
          treatmentName: localizedName(treatment.nameEn, treatment.namePl),
          contraindication: treatment.contraindication,
          device: treatment.device,
          bodyAreaId: '',
          outcome: 'done',
          response: '',
          deviceSettingSets,
          recalledFromLast,
        },
      ];
    });
  }

  function buildPayload(complete: boolean): SessionNoteWritePayload {
    const parsedPain = painScore.trim() === '' ? null : Number(painScore);
    return {
      appointmentId: linkedAppointmentId,
      painScore: parsedPain === null || Number.isNaN(parsedPain) ? null : parsedPain,
      assessment: assessment.trim() === '' ? null : assessment,
      privateNotes: privateNotes.trim() === '' ? null : privateNotes,
      bodyAreaIds,
      complete,
      treatments: treatments.map(function toInput(treatment) {
        return {
          treatmentId: treatment.treatmentId,
          bodyAreaId: treatment.bodyAreaId === '' ? null : treatment.bodyAreaId,
          outcome: treatment.outcome,
          response: treatment.response.trim() === '' ? null : treatment.response,
          deviceSettings: flattenDeviceSettingSets(treatment.deviceSettingSets),
        };
      }),
    };
  }

  async function persist(complete: boolean): Promise<SessionNote> {
    if (resolvedNoteId === null) {
      const created = await createMutation.mutateAsync({
        patientId: resolvedPatientId,
        payload: buildPayload(complete),
      });
      setResolvedNoteId(created.id);
      setLinkedAppointmentId(created.appointmentId);
      setPhotos(created.photos);
      setStatus(created.status);
      navigate(
        generatePath(APPLICATION_ROUTES.workspacePatientSessionNote, {
          patientId: resolvedPatientId,
          noteId: created.id,
        }),
        { replace: true }
      );
      return created;
    }
    const updated = await updateMutation.mutateAsync({
      noteId: resolvedNoteId,
      payload: buildPayload(complete),
    });
    setPhotos(updated.photos);
    setStatus(updated.status);
    setLinkedAppointmentId(updated.appointmentId);
    return updated;
  }

  async function addPhoto(file: File) {
    try {
      const persisted = await persist(status === 'completed');
      const withPhoto = await uploadPhotoMutation.mutateAsync({ noteId: persisted.id, file });
      setPhotos(withPhoto.photos);
    } catch {
      showErrorNotification(t('patients:sessionNote.photoError'));
    }
  }

  async function removePhoto(photoId: string) {
    if (resolvedNoteId === null) {
      return;
    }
    try {
      const updated = await removePhotoMutation.mutateAsync({ noteId: resolvedNoteId, photoId });
      setPhotos(updated.photos);
    } catch {
      showErrorNotification(t('patients:sessionNote.photoError'));
    }
  }

  function handlePhotoSelected(event: React.ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;
    if (files === null) {
      return;
    }
    const selected = files.item(0);
    if (selected !== null) {
      void addPhoto(selected);
    }
    event.target.value = '';
  }

  async function saveAsDraft() {
    setPendingSaveAction('draft');
    try {
      await persist(false);
      setBaselineSnapshot(null);
      showSuccessNotification(t('patients:sessionNote.saved'));
    } catch {
      showErrorNotification(t('patients:sessionNote.saveError'));
    } finally {
      setPendingSaveAction(null);
    }
  }

  function applyDraftFromAssistant(draft: SessionNoteDraft) {
    const updated = applySessionNoteDraft(
      { painScore, assessment, privateNotes, bodyAreaIds, treatments },
      draft,
      { library: libraryTreatments, createTreatmentKey: nextTreatmentKey, localizedName }
    );
    setPainScore(updated.painScore);
    setAssessment(updated.assessment);
    setPrivateNotes(updated.privateNotes);
    setBodyAreaIds(updated.bodyAreaIds);
    setTreatments(updated.treatments);
  }

  async function saveFromAssistant(): Promise<boolean> {
    setPendingSaveAction('draft');
    try {
      await persist(status === 'completed');
      setBaselineSnapshot(null);
      showSuccessNotification(t('patients:sessionNote.saved'));
      return true;
    } catch {
      showErrorNotification(t('patients:sessionNote.saveError'));
      return false;
    } finally {
      setPendingSaveAction(null);
    }
  }

  useAssistantSessionNoteForm(resolvedPatientId, {
    applyDraft: applyDraftFromAssistant,
    saveAsDraft: saveFromAssistant,
    hasUnsavedChanges: function reportUnsavedChanges() {
      return isDirty;
    },
  });

  async function saveNote() {
    setPendingSaveAction('complete');
    try {
      await persist(true);
      setBaselineSnapshot(null);
      showSuccessNotification(t('patients:sessionNote.saved'));
    } catch {
      showErrorNotification(t('patients:sessionNote.saveError'));
    } finally {
      setPendingSaveAction(null);
    }
  }

  function handleSaveNoteClick() {
    if (treatments.length === 0) {
      setIsSaveAsDraftConfirmOpen(true);
      return;
    }
    void saveNote();
  }

  const previousNote = (
    patientNotesQuery.data === undefined ? [] : patientNotesQuery.data.items
  ).find(function isOther(summary) {
    return summary.id !== resolvedNoteId;
  });

  const isBusy =
    createMutation.isPending ||
    updateMutation.isPending ||
    uploadPhotoMutation.isPending ||
    removePhotoMutation.isPending;
  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isCompleted = status === 'completed';
  const patientName = patientQuery.data === undefined ? '' : patientQuery.data.name;
  const statusBadgeTone: BadgeTone = isCompleted ? 'success' : 'warning';
  const statusBadgeLabel = isCompleted
    ? t('patients:sessionNote.statusCompleted')
    : t('patients:sessionNote.statusLineDraft');

  const isLoading =
    treatmentsQuery.isLoading ||
    bodyAreasQuery.isLoading ||
    (!isNew && noteQuery.isLoading) ||
    !isInitialized;

  const currentSnapshot = JSON.stringify({
    painScore,
    assessment,
    privateNotes,
    bodyAreaIds,
    treatments,
  });
  const isDirty =
    isInitialized && baselineSnapshot !== null && currentSnapshot !== baselineSnapshot;

  useEffect(
    function captureBaselineSnapshot() {
      if (isInitialized && baselineSnapshot === null) {
        setBaselineSnapshot(currentSnapshot);
      }
    },
    [isInitialized, baselineSnapshot, currentSnapshot]
  );

  useEffect(
    function guardAgainstLosingUnsavedChanges() {
      function handleBeforeUnload(beforeUnloadEvent: BeforeUnloadEvent) {
        if (isDirty === false) {
          return;
        }
        beforeUnloadEvent.preventDefault();
        beforeUnloadEvent.returnValue = '';
      }
      window.addEventListener('beforeunload', handleBeforeUnload);
      return function removeGuard() {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    },
    [isDirty]
  );

  function navigateBackToPatient() {
    navigate(
      generatePath(APPLICATION_ROUTES.workspacePatientDetail, {
        patientId: resolvedPatientId,
      })
    );
  }

  function handleBackClick() {
    if (isDirty) {
      setIsLeaveConfirmOpen(true);
      return;
    }
    navigateBackToPatient();
  }

  const footer = isLoading ? undefined : (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-2 min-w-0 flex-wrap">
        <Badge tone={statusBadgeTone} size="md" className="hidden sm:inline-flex">
          {statusBadgeLabel}
        </Badge>
        {isDirty ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-zoowarningtext">
            <Icon name="AlertCircle" />
            {t('consultations:editor.unsavedChanges')}
          </span>
        ) : null}
      </div>
      <div className="flex gap-3 w-full sm:w-auto">
        {isCompleted ? null : (
          <Button
            variant="outline"
            onClick={function onSaveAsDraft() {
              void saveAsDraft();
            }}
            disabled={isBusy}
            isLoading={pendingSaveAction === 'draft' && isSaving}
            className="flex flex-1 sm:flex-none items-center justify-center gap-2"
          >
            {t('patients:sessionNote.saveAsDraft')}
          </Button>
        )}
        <Button
          onClick={handleSaveNoteClick}
          disabled={isBusy}
          isLoading={pendingSaveAction === 'complete' && isSaving}
          className="flex flex-1 sm:flex-none items-center justify-center gap-2"
        >
          {t('patients:sessionNote.save')}
        </Button>
      </div>
    </div>
  );

  return (
    <PageContainer footer={footer} width="wide">
      <Button
        variant="link"
        onClick={handleBackClick}
        className="text-zoomuted hover:text-zooprimary transition-colors mb-4"
      >
        <Icon name="ArrowLeft" />
        {t('patients:sessionNote.back')}
      </Button>

      {(!isNew && noteQuery.isError) || treatmentsQuery.isError || bodyAreasQuery.isError ? (
        <QueryErrorState
          onRetry={function retry() {
            treatmentsQuery.refetch();
            bodyAreasQuery.refetch();
            noteQuery.refetch();
          }}
        />
      ) : isLoading ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner className="w-7 h-7" />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2 mb-5">
            <div>
              <h1 className="text-2xl font-bold text-zooindigo">
                {isNew && resolvedNoteId === null
                  ? t('patients:sessionNote.newTitle')
                  : t('patients:sessionNote.editTitle')}
              </h1>
              {patientName.length === 0 ? null : (
                <p className="text-sm text-zoomuted mt-0.5">{patientName}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Badge tone={statusBadgeTone} size="md">
                {statusBadgeLabel}
              </Badge>
              {previousNote === undefined ? null : (
                <Button
                  variant="link"
                  onClick={function openPrevious() {
                    navigate(
                      generatePath(APPLICATION_ROUTES.workspacePatientSessionNote, {
                        patientId: resolvedPatientId,
                        noteId: previousNote.id,
                      })
                    );
                  }}
                  className="text-xs font-semibold"
                >
                  <Icon name="Clock" />
                  {t('patients:sessionNote.openPreviousNote')}
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-5">
            {isCompleted ? (
              <div className="bg-zoosuccessbg rounded-2xl p-4 flex items-start gap-3">
                <Icon name="CheckCircle" className="text-zoosuccesstext text-lg mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-zooindigo">
                    {t('patients:sessionNote.completedBannerTitle')}
                  </p>
                  <p className="text-xs text-zoomuted mt-0.5">
                    {t('patients:sessionNote.completedBanner')}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="chip"
                  onClick={handleBackClick}
                  className="shrink-0"
                >
                  {t('patients:sessionNote.backToPatient')}
                </Button>
              </div>
            ) : null}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
              <div className="lg:col-span-2 space-y-5">
                <Card>
                  <p className="text-sm font-bold text-zooindigo">
                    {t('patients:sessionNote.painScore')}
                  </p>
                  <span className="block text-[11px] text-zoomuted mt-0.5">
                    {t('patients:sessionNote.painScoreHint')}
                  </span>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {PAIN_SCORES.map(function renderScore(score) {
                      const value = String(score);
                      const isSelected = painScore === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          disabled={isBusy}
                          aria-pressed={isSelected}
                          onClick={function pickScore() {
                            setPainScore(isSelected ? '' : value);
                          }}
                          className={
                            'w-9 h-9 rounded-lg text-sm font-bold border transition-colors disabled:opacity-50 ' +
                            (isSelected
                              ? 'bg-zooprimary text-zoowhite border-zooprimary'
                              : 'bg-zoowhite text-zooindigo border-zooborder')
                          }
                        >
                          {score}
                        </button>
                      );
                    })}
                  </div>
                </Card>

                <Card>
                  <p className="text-sm font-bold text-zooindigo mb-2">
                    {t('patients:sessionNote.bodyAreas')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {bodyAreas.map(function renderChip(area) {
                      const isSelected = bodyAreaIds.includes(area.id);
                      return (
                        <button
                          key={area.id}
                          type="button"
                          disabled={isBusy}
                          onClick={function toggle() {
                            toggleBodyArea(area.id);
                          }}
                          className={
                            'px-3 py-1 rounded-full text-xs font-semibold border ' +
                            (isSelected
                              ? 'bg-zooprimary text-zoowhite border-zooprimary'
                              : 'bg-zoowhite text-zooindigo border-zooborder')
                          }
                        >
                          {localizedName(area.nameEn, area.namePl)}
                        </button>
                      );
                    })}
                  </div>
                </Card>

                <Card>
                  <p className="text-sm font-bold text-zooindigo mb-3">
                    {t('patients:sessionNote.treatments')}
                  </p>
                  <SearchableSelect
                    inputId="session-note-treatment-picker"
                    ariaLabel={t('patients:sessionNote.addTreatment')}
                    placeholder={t('patients:sessionNote.addTreatment')}
                    value={pickerValue}
                    isDisabled={isBusy}
                    options={libraryTreatments.map(function toOption(treatment) {
                      return {
                        value: treatment.id,
                        label: localizedName(treatment.nameEn, treatment.namePl),
                      };
                    })}
                    onChange={function pickTreatment(nextValue) {
                      const treatment = libraryTreatments.find(function match(candidate) {
                        return candidate.id === nextValue;
                      });
                      if (treatment !== undefined) {
                        void addTreatment(treatment);
                      }
                      setPickerValue('');
                    }}
                  />
                  {treatments.length === 0 ? (
                    <p className="text-xs text-zoomuted mt-3">
                      {t('patients:sessionNote.noTreatments')}
                    </p>
                  ) : (
                    <div className="space-y-3 mt-3">
                      {treatments.map(function renderTreatment(treatment) {
                        return (
                          <TreatmentRow
                            key={treatment.key}
                            treatment={withLocalizedTreatmentName(treatment)}
                            isDisabled={isBusy}
                            bodyAreaOptions={bodyAreas.map(function toOption(area) {
                              return {
                                value: area.id,
                                label: localizedName(area.nameEn, area.namePl),
                              };
                            })}
                            onChange={function change(updated) {
                              setTreatments(function apply(current) {
                                return current.map(function replace(existing) {
                                  return existing.key === updated.key ? updated : existing;
                                });
                              });
                            }}
                            onRemove={function remove() {
                              setTreatments(function drop(current) {
                                return current.filter(function keep(existing) {
                                  return existing.key !== treatment.key;
                                });
                              });
                            }}
                          />
                        );
                      })}
                    </div>
                  )}
                </Card>

                <Card className="space-y-4">
                  <label className="block text-xs font-semibold text-zoomuted">
                    {t('patients:sessionNote.assessment')}
                    <AutoGrowTextarea
                      value={assessment}
                      disabled={isBusy}
                      rows={3}
                      placeholder={t('patients:sessionNote.assessmentPlaceholder')}
                      onChange={function changeAssessment(event) {
                        setAssessment(event.target.value);
                      }}
                      className="mt-1 w-full rounded-xl border border-zooborder bg-zoowhite px-3 py-2 text-sm text-zooindigo"
                    />
                  </label>
                  <label className="block text-xs font-semibold text-zooviolet">
                    {t('patients:sessionNote.privateNotes')}
                    <AutoGrowTextarea
                      value={privateNotes}
                      disabled={isBusy}
                      rows={2}
                      onChange={function changePrivate(event) {
                        setPrivateNotes(event.target.value);
                      }}
                      className="mt-1 w-full rounded-xl border border-zooviolet bg-zoovpalelavender bg-opacity-30 px-3 py-2 text-sm text-zooindigo"
                    />
                    <span className="block text-[11px] text-zooviolet mt-1">
                      {t('patients:sessionNote.privateNotesHint')}
                    </span>
                  </label>
                </Card>
              </div>

              <div className="space-y-5">
                <SummaryCard noteId={resolvedNoteId} />

                <Card>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-zooindigo">
                      {t('patients:sessionNote.photos')}
                    </p>
                    <Button
                      variant="link"
                      disabled={isBusy}
                      onClick={function selectPhoto() {
                        if (photoInputRef.current !== null) {
                          photoInputRef.current.click();
                        }
                      }}
                      className="text-xs"
                    >
                      <Icon name="Upload" />
                      {t('patients:sessionNote.addPhoto')}
                    </Button>
                  </div>
                  {photos.length === 0 ? null : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-3">
                      {photos.map(function renderPhoto(photo) {
                        const isRemovingThisPhoto =
                          removePhotoMutation.isPending &&
                          removePhotoMutation.variables !== undefined &&
                          removePhotoMutation.variables.photoId === photo.id;
                        return (
                          <div key={photo.id} className="relative group">
                            <button
                              type="button"
                              onClick={function preview() {
                                setPreviewedPhoto(photo);
                              }}
                              className="block w-full"
                            >
                              <img
                                src={photo.url}
                                alt=""
                                className="w-full h-24 object-cover rounded-xl border border-zooborder"
                              />
                            </button>
                            <Button
                              variant="destructiveGhost"
                              size="icon"
                              disabled={isBusy}
                              isLoading={isRemovingThisPhoto}
                              aria-label={t('patients:sessionNote.removePhoto')}
                              onClick={function remove(clickEvent) {
                                clickEvent.stopPropagation();
                                void removePhoto(photo.id);
                              }}
                              className="absolute top-1 right-1 w-auto h-auto rounded-full p-1 bg-zoowhite shadow text-zooerrortext"
                            >
                              {isRemovingThisPhoto ? null : <Icon name="Trash" />}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept={buildAcceptAttribute(MEDIA_IMAGE_CONTENT_TYPES)}
                    className="hidden"
                    onChange={handlePhotoSelected}
                  />
                </Card>
              </div>
            </div>
          </div>

          <FilePreviewModal
            isOpen={previewedPhoto !== null}
            onClose={function closePreview() {
              setPreviewedPhoto(null);
            }}
            url={previewedPhoto === null ? '' : previewedPhoto.url}
            filename={t('patients:sessionNote.photo')}
            kind="image"
          />

          {isLeaveConfirmOpen === true ? (
            <ConfirmDialog
              title={t('consultations:editor.leaveTitle')}
              description={t('consultations:editor.leaveMessage')}
              confirmLabel={t('consultations:editor.leaveConfirm')}
              tone="danger"
              iconName="AlertCircle"
              onConfirm={navigateBackToPatient}
              onCancel={function cancelLeave() {
                setIsLeaveConfirmOpen(false);
              }}
            />
          ) : null}

          {isSaveAsDraftConfirmOpen === true ? (
            <ConfirmDialog
              title={t('patients:sessionNote.saveAsDraftConfirmTitle')}
              description={t('patients:sessionNote.saveAsDraftConfirmDescription')}
              confirmLabel={t('patients:sessionNote.saveAsDraft')}
              iconName="AlertCircle"
              isConfirming={pendingSaveAction === 'draft' && isSaving}
              onConfirm={function confirmSaveAsDraft() {
                setIsSaveAsDraftConfirmOpen(false);
                void saveAsDraft();
              }}
              onCancel={function cancelSaveAsDraft() {
                setIsSaveAsDraftConfirmOpen(false);
              }}
            />
          ) : null}
        </>
      )}
    </PageContainer>
  );
}

function findSettingFieldType(device: DeviceBrief | null, fieldId: string): string | null {
  if (device === null) {
    return null;
  }
  const field = device.settingFields.find(function matchesField(candidate) {
    return candidate.id === fieldId;
  });
  return field === undefined ? null : field.fieldType;
}
